// Static legal document content - matches /legal/terms page content
export const legalDocuments = {
  terms: {
    title: "Instructor Terms and Conditions",
    version: "3.0",
    effectiveDate: "January 15, 2026",
    lastUpdatedAt: "January 15, 2026",
    content: `
# Instructor Terms and Conditions

**Relaxing Routine**

**Last Updated:** 15 January 2026

## 1. Introduction

Welcome to Relaxing Routine ("Relaxing Routine", "we", "us", "our", or the "Platform").

These Instructor Terms and Conditions ("Terms") govern your access to and use of the Relaxing Routine website, applications, and services (collectively, the "Services") as an Instructor.

By registering, accessing, or using the Services as an Instructor, you acknowledge that you have read, understood, and agree to be legally bound by these Terms.

**IF YOU DO NOT AGREE TO THESE TERMS, YOU MUST NOT REGISTER OR USE THE SERVICES.**

## 2. Nature of the Platform & Relationship

### 2.1 Platform Role (Intermediary Only)

Relaxing Routine is a technology platform that facilitates connections between independent wellness instructors ("Instructors") and users ("Students").

Relaxing Routine does not provide wellness, fitness, medical, therapeutic, or training services of any kind.

### 2.2 Independent Contractor Status

You acknowledge and agree that:
- You are an independent contractor
- You are not an employee, agent, partner, or representative of Relaxing Routine
- You have no authority to bind Relaxing Routine in any manner
- Relaxing Routine does not control how you conduct sessions, teach classes, or interact with Students.

### 2.3 No Medical Advice

All sessions offered through the Platform are for general wellness and educational purposes only.

Relaxing Routine does not provide medical advice, diagnosis, or treatment and does not verify the medical suitability of any session.

## 3. Instructor Obligations & Representations

By registering as an Instructor, you represent, warrant, and agree that:

### 3.1 Qualifications & Certifications

You possess all necessary qualifications, certifications, licenses, permits, and insurance required to legally offer the services you provide. You agree to submit proof upon request.

### 3.2 Legal & Regulatory Compliance

You will comply with all applicable laws, including but not limited to:
- Tax laws
- GST regulations
- Business registration requirements
- Consumer protection laws

### 3.3 Professional Conduct

You will:
- Treat Students with respect and professionalism
- Not engage in harassment, abuse, discrimination, or inappropriate conduct
- Not engage in illegal, misleading, or harmful behavior

### 3.4 Health Screening & Safety Responsibility

You are solely responsible for:
- Assessing Student readiness
- Obtaining waivers or disclosures
- Modifying activities to reduce risk

### 3.5 Assumption of Risk & Injury Disclaimer

You acknowledge that wellness, yoga, fitness, meditation, breathing, and physical activities involve inherent risks, including injury, illness, emotional distress, or death.

You voluntarily assume full responsibility for all risks arising from sessions you conduct.

Relaxing Routine shall not be liable for any injury, health issue, accident, or adverse outcome occurring during or after any session.

All such claims are strictly between the Instructor and the Student.

### 3.6 No Supervision or Control

Relaxing Routine does not supervise, monitor, or control:
- Session content
- Teaching methods
- Safety practices
- Student interactions

All responsibility rests solely with the Instructor.

## 4. Account Registration & Security

### 4.1 Accurate Information

You must provide accurate, complete, and current information and keep it updated.

### 4.2 Account Responsibility

You are solely responsible for:
- Maintaining account confidentiality
- All activity conducted under your account

Relaxing Routine is not responsible for unauthorized access caused by your negligence.

## 5. Payments, Fees & Taxes

### 5.1 Platform Fees

Relaxing Routine currently operates on an 80% instructor / 20% platform fee model.
Platform fees are subject to change at Relaxing Routine’s discretion, with reasonable prior notice provided to Instructors.

### 5.2 Payment Processing

Payments are processed via third-party providers. You agree to comply with their terms.

Relaxing Routine does not control payment processor failures or delays.

### 5.3 Payouts

Payouts are subject to:
- Successful class completion
- Refund policies
- Verification checks
- Minimum payout thresholds
- Payout schedules determined by Relaxing Routine

### 5.4 Taxes

You are solely responsible for:
- Income tax
- GST or indirect taxes
- Any statutory filings related to your earnings

Relaxing Routine is not responsible for your tax compliance.

## 6. Content & Intellectual Property

### 6.1 Instructor Content

You retain ownership of your content.
You grant Relaxing Routine a non-exclusive, worldwide, royalty-free license to use your content for operating and promoting the Platform.

### 6.2 Platform Intellectual Property

All platform software, branding, design, and data belong exclusively to Relaxing Routine.

You may not copy, reverse engineer, or exploit the Platform.

### 6.3 Session Recording & Privacy

Relaxing Routine does not control or monitor session recordings.

You are solely responsible for:
- Obtaining consent
- Compliance with privacy laws
- Handling misuse or disputes

Relaxing Routine bears no liability for unauthorized recording or distribution.

## 7. Liability, Indemnity & Legal Protection

### 7.1 Disclaimer of Warranties

THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.

### 7.2 Limitation of Liability

TO THE MAXIMUM EXTENT PERMITTED BY LAW, RELAXING ROUTINE SHALL NOT BE LIABLE FOR ANY DIRECT OR INDIRECT DAMAGES, INCLUDING BUT NOT LIMITED TO PERSONAL INJURY, LOSS OF PROFITS, LOSS OF DATA, OR SERVICE INTERRUPTION ARISING FROM:
- USE OF THE PLATFORM
- SESSIONS CONDUCTED BY INSTRUCTORS
- INSTRUCTOR–STUDENT INTERACTIONS
- NEGLIGENCE OR MISCONDUCT OF USERS

TOTAL LIABILITY SHALL NOT EXCEED PLATFORM FEES PAID BY THE INSTRUCTOR IN THE PRECEDING SIX (6) MONTHS.

### 7.3 Release

You release Relaxing Routine from all claims arising from disputes between you and Students.

### 7.4 Indemnification

You agree to indemnify and hold harmless Relaxing Routine from all claims, damages, losses, or legal expenses arising from:
- Your sessions
- Your conduct
- Your legal violations
- Student complaints or injuries

### 7.5 No Responsibility for Off-Platform Conduct

Relaxing Routine is not responsible for:
- Private communications
- Offline meetings
- Payments outside the Platform

All such risks are assumed by the Instructor.

## 8. Suspension & Termination

### 8.1 By Instructor

You may stop using the Services at any time.

### 8.2 By Relaxing Routine

Relaxing Routine may suspend or terminate your account:
- Without notice
- For any violation
- To protect platform integrity

No liability shall arise from termination.

## 9. Governing Law & Dispute Resolution

### 9.1 Governing Law

These Terms are governed by the laws of India.

### 9.2 Arbitration

All disputes shall be resolved by binding arbitration in Bengaluru, Karnataka, under the Arbitration and Conciliation Act, 1996.

## 10. Changes to Terms

Relaxing Routine may update these Terms at any time. Continued use constitutes acceptance.

## 11. Contact Information

**Legal Contact:**
📧 support@relaxingroutine.com

📍 C 986, Patel Nagar, Bareilly, Uttar Pradesh, India

**Legal Entity:** Aarav Gupta
`,
  },
  privacy: {
    title: "Privacy Policy",
    version: "3.0",
    effectiveDate: "January 15, 2026",
    lastUpdatedAt: "January 15, 2026",
    content: `
# Privacy Policy

**Relaxing Routine**

**Last Updated:** 15 January 2026

## 1. Introduction

Relaxing Routine ("Relaxing Routine", "we", "our", or "us") is committed to protecting your privacy and personal data.

This Privacy Policy explains how we collect, use, store, disclose, and protect information when you register as an Instructor or use our website, applications, and services (collectively, the "Platform").

By accessing or using the Platform, you consent to the collection and use of information in accordance with this Privacy Policy.
If you do not agree, please do not use the Platform.

## 2. Scope of This Policy

This Privacy Policy applies to:

- Instructors registering on the Platform
- Use of instructor dashboards, bookings, earnings, and analytics
- Communications with Relaxing Routine

This policy does not apply to:

- Student privacy policies
- Third-party websites or services
- Off-platform interactions between instructors and students

## 3. Information We Collect

We follow a data minimization principle, collecting only what is necessary to operate a wellness marketplace and comply with legal obligations.

### 3.1 Personal & Identity Information

- Full name
- Date of birth (if required for verification)
- Gender (optional)
- Profile photo
- Government-issued ID (Aadhaar, Passport, PAN) for KYC and compliance

### 3.2 Contact Information

- Email address
- Phone number
- City, state, and country

### 3.3 Professional Information

- Certifications and qualifications
- Areas of expertise
- Years of experience
- Biography and profile details
- Uploaded documents (certifications, insurance, ID)

### 3.4 Financial & Tax Information

- Bank account details
- UPI ID (if applicable)
- PAN / Tax Identification details
- Payout and transaction history

### 3.5 Usage & Technical Information

- IP address
- Browser type and device details
- Log data (access times, pages viewed)
- Cookies and similar technologies

### 3.6 Communications

- Support messages
- Platform notifications
- Communications sent through the Platform

⚠️ **Note:**
Relaxing Routine does not monitor private communications conducted outside the Platform (e.g., WhatsApp, email).

## 4. How We Use Your Information

We use your information strictly for legitimate business purposes, including:

- Account creation and management
- Listing and promoting your classes
- Facilitating bookings and sessions
- Processing payments and payouts
- Identity verification and fraud prevention
- Customer support and dispute handling
- Compliance with tax, legal, and regulatory requirements
- Platform analytics and improvement
- Sending transactional communications

Marketing communications are optional and you may opt out at any time.

## 5. Legal Basis for Processing (India-Aligned)

We process your data based on:

- Your consent
- Performance of a contract
- Compliance with legal obligations
- Legitimate business interests (platform safety, fraud prevention)

## 6. Sharing & Disclosure of Information

We may share your data only in the following cases:

### 6.1 With Students

- Public profile information (name, bio, experience, ratings)
- Limited contact details only after confirmed bookings, when necessary

### 6.2 With Service Providers

We use trusted third parties for:

- Payment processing (e.g., Razorpay, Stripe)
- Cloud hosting and storage
- Email and notification delivery
- Analytics and performance monitoring

These providers are contractually obligated to protect your data.

### 6.3 Legal & Regulatory Disclosure

We may disclose information if required by:

- Law
- Court orders
- Government or regulatory authorities

### 6.4 Business Transfers

In the event of a merger, acquisition, or sale of assets, your data may be transferred as part of the transaction.

## 7. Data Retention

We retain personal data only for as long as necessary to:

- Provide services
- Meet legal and tax obligations
- Resolve disputes
- Enforce agreements

Some financial and transaction records may be retained longer as required by law.

## 8. Data Security

We implement reasonable technical and organizational measures, including:

- Encryption
- Secure servers
- Access controls
- Limited internal access

However, no system is completely secure, and we cannot guarantee absolute security.

## 9. Your Rights

Subject to applicable law, you have the right to:

- Access your personal data
- Correct inaccurate information
- Request deletion (subject to legal retention)
- Withdraw consent (may limit service access)
- Restrict or object to certain processing

Requests can be made at:
📧 privacy@relaxingroutine.com

## 10. Cookies & Tracking

We use cookies and similar technologies to:

- Improve user experience
- Analyze platform usage
- Maintain session security

You may control cookies through your browser settings.

## 11. Third-Party Links

The Platform may contain links to third-party websites.
We are not responsible for their privacy practices or content.

## 12. Children's Privacy

The Platform is not intended for individuals under 18 years of age.
We do not knowingly collect personal data from minors.

## 13. Changes to This Privacy Policy

We may update this Privacy Policy periodically.
Updates will be posted on this page with a revised "Last Updated" date.

Continued use of the Platform constitutes acceptance of the updated policy.

## 14. Contact Information

Relaxing Routine – Privacy Team
📧 Email: support@relaxingroutine.com

📍 C 986, Patel Nagar, Bareilly, Uttar Pradesh, India

**Legal Entity:** Aarav Gupta
`,
  },
  refund: {
    title: "Refund Policy",
    version: "1.0",
    effectiveDate: "December 1, 2024",
    lastUpdatedAt: "December 1, 2024",
    content: `<h1>Refund Policy</h1>

<p><strong>Effective Date: December 1, 2024</strong></p>

<h2>1. General Policy</h2>
<p>At Relaxing Routine, we strive to provide excellent wellness services. This Refund Policy outlines the circumstances under which refunds may be issued.</p>

<h2>2. Class Cancellations</h2>
<ul>
<li>If an instructor cancels a class, you will receive a full refund</li>
<li>Refunds are processed within 5-7 business days</li>
<li>You will be notified via email when a refund is processed</li>
</ul>

<h2>3. Student-Initiated Cancellations</h2>
<ul>
<li>Cancellations 24+ hours before class: Full refund</li>
<li>Cancellations 12-24 hours before class: 50% refund</li>
<li>Cancellations less than 12 hours before class: No refund</li>
</ul>

<h2>4. No-Shows</h2>
<p>If you fail to attend a scheduled class without prior cancellation, no refund will be issued.</p>

<h2>5. Technical Issues</h2>
<p>If technical issues on our platform prevent you from attending a class, contact support within 24 hours for a full refund.</p>

<h2>6. Subscription Refunds</h2>
<ul>
<li>Monthly subscriptions: Cancel anytime; no prorated refunds</li>
<li>Annual subscriptions: Refunds available within 30 days of purchase</li>
</ul>

<h2>7. How to Request a Refund</h2>
<p>To request a refund:</p>
<ol>
<li>Contact our support team at support@relaxingroutine.com</li>
<li>Provide your booking details and reason for refund</li>
<li>Allow 2-3 business days for review</li>
</ol>

<h2>8. Exceptions</h2>
<p>Refunds may not be available for:</p>
<ul>
<li>Completed classes</li>
<li>Promotional or discounted bookings (unless specified)</li>
<li>Gift certificates or credits</li>
</ul>

<h2>9. Contact Us</h2>
<p>Questions about refunds? Contact us at support@relaxingroutine.com</p>

<hr />
<p><em>Last updated: December 1, 2024</em></p>`,
  },
  cookie: {
    title: "Cookie Policy",
    version: "1.0",
    effectiveDate: "December 1, 2024",
    lastUpdatedAt: "December 1, 2024",
    content: `<h1>Cookie Policy</h1>

<p><strong>Effective Date: December 1, 2024</strong></p>

<h2>1. What Are Cookies</h2>
<p>Cookies are small text files stored on your device when you visit our website. They help us provide you with a better experience.</p>

<h2>2. Types of Cookies We Use</h2>

<h3>Essential Cookies</h3>
<p>Required for the platform to function properly. These cannot be disabled.</p>
<ul>
<li>Authentication cookies</li>
<li>Security cookies</li>
<li>Session management</li>
</ul>

<h3>Analytics Cookies</h3>
<p>Help us understand how visitors use our platform.</p>
<ul>
<li>Page views and navigation</li>
<li>User behavior analysis</li>
<li>Performance monitoring</li>
</ul>

<h3>Functional Cookies</h3>
<p>Remember your preferences and settings.</p>
<ul>
<li>Language preferences</li>
<li>Display settings</li>
<li>Personalization options</li>
</ul>

<h3>Marketing Cookies</h3>
<p>Used to show relevant advertisements (with your consent).</p>

<h2>3. Third-Party Cookies</h2>
<p>We may use third-party services that set cookies:</p>
<ul>
<li>Google Analytics for website analytics</li>
<li>Payment processors for secure transactions</li>
<li>Social media platforms for sharing features</li>
</ul>

<h2>4. Managing Cookies</h2>
<p>You can control cookies through:</p>
<ul>
<li>Browser settings (to block or delete cookies)</li>
<li>Our cookie consent banner</li>
<li>Privacy settings in your account</li>
</ul>

<h2>5. Impact of Disabling Cookies</h2>
<p>Disabling certain cookies may:</p>
<ul>
<li>Limit platform functionality</li>
<li>Prevent access to certain features</li>
<li>Reduce personalization</li>
</ul>

<h2>6. Updates to Cookie Policy</h2>
<p>We may update this Cookie Policy from time to time. Check this page periodically for changes.</p>

<h2>7. Contact Us</h2>
<p>Questions about cookies? Email us at privacy@relaxingroutine.com</p>

<hr />
<p><em>Last updated: December 1, 2024</em></p>`,
  },
  studentTerms: {
    title: "Student Terms and Conditions",
    version: "1.0",
    effectiveDate: "January 15, 2026",
    lastUpdatedAt: "January 15, 2026",
    content: `
# Student Terms and Conditions

**Relaxing Routine**

**Last Updated:** 15 January 2026

## 1. Introduction

Welcome to Relaxing Routine ("Relaxing Routine", "we", "our", "us", or the "Platform").

These Terms and Conditions ("Terms") govern your access to and use of our website, mobile applications, and services (collectively, the "Services") as a Student.

By registering, booking a class, or using the Platform, you agree to be bound by these Terms.
If you do not agree, you must not use the Services.

## 2. Nature of the Platform

### 2.1 Intermediary Platform Only

Relaxing Routine is a technology marketplace that connects Students with independent wellness instructors ("Instructors").

We do not provide wellness, fitness, yoga, or medical services
We do not employ Instructors
We do not supervise or control sessions

All sessions are provided solely by Instructors.

### 2.2 No Medical Advice

The Platform does not provide medical advice, diagnosis, or treatment.

All services are for general wellness and educational purposes only.
You should consult a qualified medical professional before participating in any physical activity.

## 3. Student Responsibilities

By using the Platform, you agree that:

### 3.1 Health & Fitness Responsibility

You are solely responsible for:
- Assessing your physical and mental fitness
- Informing the Instructor of any medical conditions
- Stopping any activity that causes pain, discomfort, or distress

### 3.2 Assumption of Risk

You acknowledge that wellness, yoga, fitness, meditation, and breathing activities involve inherent risks, including injury or illness.

You voluntarily assume all risks arising from participation in any session.

Relaxing Routine shall not be liable for any injury, health issue, or adverse outcome.

### 3.3 Conduct

You agree to:
- Treat Instructors respectfully
- Not engage in harassment, abuse, or inappropriate behavior
- Follow session rules set by the Instructor

Violation may result in suspension or termination.

## 4. Bookings, Payments & Refunds

### 4.1 Bookings

Booking a class creates a binding agreement between you and the Instructor
Relaxing Routine is not a party to that agreement

### 4.2 Payments

Payments are processed through third-party payment gateways
Prices shown may include platform fees and applicable taxes (GST)
Relaxing Routine does not store your card details.

### 4.3 Refunds & Cancellations

Refund eligibility depends on the Instructor's cancellation policy
Relaxing Routine may facilitate refunds but does not guarantee them
Platform fees may be non-refundable

## 5. Session Delivery & Issues

### 5.1 Instructor Responsibility

Instructors are solely responsible for:
- Session quality
- Accuracy of content
- Safety instructions
- Timely delivery

### 5.2 No Guarantee of Outcomes

Relaxing Routine makes no guarantees regarding:
- Results
- Improvements
- Fitness or health outcomes

## 6. Recording & Privacy

Recording sessions is not allowed without explicit consent
Any misuse of recordings is strictly prohibited
Disputes regarding recordings are between Student and Instructor

## 7. Off-Platform Interactions

Relaxing Routine is not responsible for:
- Private communications
- Offline meetings
- Payments made outside the Platform

You assume all risks of off-platform interactions.

## 8. Suspension & Termination

Relaxing Routine may suspend or terminate your account:
- For violating these Terms
- For abusive or harmful conduct
- To protect platform integrity

No compensation is owed upon termination.

## 9. Limitation of Liability

To the maximum extent permitted by law:
- Relaxing Routine shall not be liable for injuries, losses, or damages
- Liability is limited to platform fees paid by you in the preceding six (6) months

## 10. Governing Law & Dispute Resolution

Governed by laws of India
Disputes resolved by arbitration in Bengaluru, Karnataka
Governed by Arbitration and Conciliation Act, 1996

## 11. Contact

📧 support@relaxingroutine.com

📍 C 986, Patel Nagar, Bareilly, Uttar Pradesh, India, India

**Legal Entity:** Aarav Gupta
`,
  },
  studentPrivacy: {
    title: "Student Privacy Policy",
    version: "2.0",
    effectiveDate: "January 15, 2026",
    lastUpdatedAt: "January 15, 2026",
    content: `
# Student Privacy Policy

**Relaxing Routine**

**Last Updated:** 15 January 2026

## 1. Introduction

Relaxing Routine respects your privacy and is committed to protecting your personal data.

This Privacy Policy explains how we collect, use, and protect your information when you use the Platform as a Student.

## 2. Information We Collect

### 2.1 Personal Information
- Name
- Email address
- Phone number
- Profile photo (optional)

### 2.2 Booking & Payment Data
- Class bookings
- Transaction history
- Payment status (handled via third-party gateways)

### 2.3 Technical Data
- IP address
- Browser & device type
- Usage logs
- Cookies

## 3. How We Use Your Information

We use your information to:
- Create and manage your account
- Process bookings and payments
- Communicate class updates
- Improve platform performance
- Comply with legal obligations

## 4. Sharing of Information

### 4.1 With Instructors
- Your name and booking details
- Limited contact information when required for class coordination

### 4.2 With Service Providers
- Payment gateways
- Cloud hosting
- Email & notification services

All providers are contractually required to protect your data.

### 4.3 Legal Requirements
We may disclose information if required by law or court order.

## 5. Data Retention

We retain data only as long as necessary for:
- Service delivery
- Legal and tax compliance
- Dispute resolution

## 6. Data Security

We use reasonable safeguards including encryption and access controls.
However, no system is 100% secure.

## 7. Your Rights

You may request:
- Access to your data
- Correction of inaccurate data
- Deletion (subject to legal limits)
- Withdrawal of consent

📧 privacy@relaxingroutine.com

## 8. Cookies

We use cookies for:
- Session management
- Analytics
- Performance optimization

You may manage cookies via browser settings.

## 9. Children’s Privacy

The Platform is not intended for users under 18 years of age.

## 10. Policy Updates

We may update this Privacy Policy periodically.
Continued use constitutes acceptance.

## 11. Contact

Relaxing Routine – Privacy Team
📧 privacy@relaxingroutine.com

📍 C 986, Patel Nagar, Bareilly, Uttar Pradesh, India, India
`,
  },
};

export type DocumentType = keyof typeof legalDocuments;
